import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute, Data, NavigationEnd, Router } from "@angular/router";
import { Browser, detect } from "detect-browser";
import { filter, map, mergeMap, tap } from "rxjs/operators";

import { Subscription, lastValueFrom } from "rxjs";
import { AuthGuardService } from "./core/core-services/services/auth-guard.service";
import { MetaService } from "./core/core-services/services/meta.service";
import { Store } from "@ngrx/store";
import { PlanState } from "./core/store/plan/plan.reducer";
import { loadUserCurrentSubscriptionApi } from "./core/store/plan/plan.actions";
import { SharedDataService } from "./core/core-services/services/shared-data.service";
import { PendingCouponService } from "./core/core-services/services/pending-coupon.service";
import { StoredDiscountService } from "./core/core-services/services/stored-discount.service";
import { ExtensionService } from "./core/core-services/services/extension.service";
import { VngTourService } from "@vengreso/angular-components";
import { BackdropComponent } from "./backdrop/backdrop.component";
import { DataService } from "./core/core-services/services/dataservices/data.service";
import { AuthService } from "./core/core-services/services/auth.service";
import { MasqueradeService } from "./core/core-services/services/masquerade.service";
import { TrackActionsAnalytics } from "./dto";
import { environment } from "src/environments/environment";
import { formatISO } from "date-fns";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  tourActive = false;
  private layoutRouter: Subscription;
  private pendingCouponRouter: Subscription;
  private browser_type: Browser;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly metaService: MetaService,
    private readonly authGuard: AuthGuardService,
    private readonly store: Store<PlanState>,
    private readonly sharedData: SharedDataService,
    private readonly extensionService: ExtensionService,
    private readonly dataService: DataService,
    private readonly authService: AuthService,
    private readonly masqueradeService: MasqueradeService,
    private readonly pendingCoupon: PendingCouponService,
    private readonly storedDiscount: StoredDiscountService,
    private tourService: VngTourService,
    private injector: Injector,
    private appRef: ApplicationRef,
    private resolver: ComponentFactoryResolver
  ) {
    authGuard.validateStatus();
    this.sharedData.deleteAttribute("advancedSearchData");

    // Store browser type
    var browser = detect();
    if (browser && (browser?.name == "edge" || browser?.name == "edge-chromium")) {
      this.browser_type = "edge";
    } else {
      this.browser_type = browser?.name as Browser;
    }
    if (this.browser_type) {
      this.sharedData.setAttribute("browser_type", this.browser_type);
    }
  }

  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.tourActive && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  async recursiveCheckExtensionAuth(): Promise<void> {
    const isSignedIn = await this.extensionService.isExtensionSignedIn();

    if (!isSignedIn) {
      setTimeout(() => {
        this.recursiveCheckExtensionAuth();
      }, 10000);
    }
  }

  ngOnInit(): void {
    // If this tab was opened in masquerade mode, set the masquerade favicon.
    // This covers browser refresh — sessionStorage survives refresh but the
    // favicon <link> is rebuilt from scratch on each page load.
    if (this.masqueradeService.isMasquerading()) {
      this.masqueradeService.setMasqueradeFavicon();
    }

    // this.addBackdrop();
    this.migrateOldStorage();
    this.migrateStoredDiscount();
    this.layoutRouter = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === "primary"),
        mergeMap((route) => route.data),
        tap(({ title, description }: Data) => {
          if (title && description) this.metaService.updateTitle(title);
          this.metaService.updateDescription(description);
        })
      )
      .subscribe();

    // Clears a pending URL-supplied coupon (issue #592) once the user has
    // navigated outside the pricing/checkout funnel, so it doesn't linger
    // in sessionStorage for some later, unrelated visit to /pricing in the
    // same tab (AC-6). A separate subscription from the one above so it
    // isn't gated by that pipeline's `route.outlet === "primary"`/`route.data`
    // filters, which a funnel route may not satisfy.
    this.pendingCouponRouter = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.pendingCoupon.clearIfOutsideFunnel(event.urlAfterRedirects);
        // Same policy, same trigger, for a manually applied discount code
        // (issue #594 decision 2): it survives in-app navigation within the
        // pricing/checkout funnel and is dropped once the user leaves it.
        this.storedDiscount.clearIfOutsideFunnel(event.urlAfterRedirects);
      });

    if (this.authGuard.isLoggedIn()) {
      this.store.dispatch(loadUserCurrentSubscriptionApi({}));
      // issue #559: refresh the per-user feature map on every app bootstrap
      // too, not only at login — a session already signed in before this
      // field existed on its cached user record otherwise never picks it up.
      this.authService.refreshUserFeatures();
    }

    this.extensionService.isExtensionInstalled().then((result) => {
      if (result) {
        this.recursiveCheckExtensionAuth();
      }
    });
  }

  private async trackFlyGrammar(data: any) {
    await lastValueFrom(
      this.dataService.post({
        url: "v1/grammar/actions",
        data: {
          ...data,
          executed_at: environment.FRONT_END_URL,
        },
        isLoader: false,
      })
    );
  }

  private async trackActions(action: TrackActionsAnalytics) {
    const data = {
      user_id: this.authService.userDetails?.id,
      browser: this.browser_type,
      domain: environment.FRONT_END_URL.replace("http://", "").replace("https://", ""),
      event_name: action,
      executed_at: formatISO(new Date(), { representation: "complete" }),
      extension_version: "app.vengreso.com",
    };

    const url = `v1/user/extension/track/${data.user_id}`;

    await lastValueFrom(
      this.dataService.post({
        url,
        data,
        isLoader: false,
      })
    );
  }

  private backdropRef!: ComponentRef<BackdropComponent>;

  private addBackdrop() {
    const factory = this.resolver.resolveComponentFactory(BackdropComponent);
    this.backdropRef = factory.create(this.injector);

    this.appRef.attachView(this.backdropRef.hostView);

    const backdropElement = (this.backdropRef.hostView as any).rootNodes[0];
    document.body.appendChild(backdropElement);
  }

  ngOnDestroy(): void {
    this.layoutRouter.unsubscribe();
    this.pendingCouponRouter.unsubscribe();
  }

  migrateOldStorage(): void {
    const oldKeys = [
      "browser_type",
      "lastExternalReferrer",
      "lastExternalReferrerTime",
      "omVisits",
      "omVisitsFirst",
      "onTrial",
      "planId",
      "planIdType",
      "usedTrial",
    ];

    oldKeys.forEach((key) => {
      const storedValue = localStorage.getItem(key);

      if (storedValue !== null) {
        let parsedValue;

        try {
          parsedValue = JSON.parse(storedValue);
        } catch (error) {
          parsedValue = storedValue;
        }

        try {
          this.sharedData.setAttribute(key, parsedValue);
        } catch (error) {
          return;
        }

        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Issue #594 decision 2: a manually applied discount code used to persist
   * in `SharedDataService`'s `vengreso.*`-prefixed localStorage (or
   * sessionStorage while masquerading) forever — surviving a closed tab and
   * a brand-new session, which is what let a code (and, before this fix, a
   * dollar amount) from an old visit reappear for a different plan. The
   * code now lives only in `StoredDiscountService`'s session-only storage.
   * Runs once per app bootstrap ("on first load"): migrates the CODE only
   * — never the old `discount` dollar amount, which must never be carried
   * forward onto a new plan — into the new store, then removes both legacy
   * keys from both storages so a stale value from a pre-#594 build can
   * never resurface. `PaymentDetailsComponent`'s own re-validation on load
   * (never trusting a restored amount) covers the code once migrated.
   */
  migrateStoredDiscount(): void {
    const LEGACY_CODE_KEY = "vengreso.discountCode";
    const LEGACY_AMOUNT_KEY = "vengreso.discount";

    [localStorage, sessionStorage].forEach((storage) => {
      try {
        const rawCode = storage.getItem(LEGACY_CODE_KEY);
        if (rawCode != null && rawCode !== "undefined" && rawCode !== "null") {
          let code: unknown;
          try {
            code = JSON.parse(rawCode);
          } catch {
            code = rawCode;
          }
          if (typeof code === "string" && code.trim()) {
            this.storedDiscount.set(code);
          }
        }
      } catch (error) {
        // Storage unavailable (private mode, quota) — nothing to migrate.
      }

      try {
        storage.removeItem(LEGACY_CODE_KEY);
        storage.removeItem(LEGACY_AMOUNT_KEY);
      } catch (error) {
        // Storage unavailable — nothing more to clean up.
      }
    });
  }

  ngAfterViewInit() {
    this.tourService.start$.subscribe(() => {
      this.tourActive = true;
      this.setTourBodyClass(true);
    });

    this.tourService.end$.subscribe(() => {
      this.tourActive = false;
      this.setTourBodyClass(false);
    });
  }

  /**
   * Marks <body> while a walk-me step is on screen so global CSS can react to
   * it. It has to be <body>, not a host class: the overlays this state exists
   * for — vng-select/vng-autocomplete panels, and the tour's own host — are all
   * appended to document.body, outside app-root's subtree. See the
   * `--vng-z-select-panel` override in app.scss for what it drives.
   */
  private setTourBodyClass(active: boolean) {
    document.body.classList.toggle("fms-tour-active", active);
  }
}

class FlyGrammarAction {
  constructor(
    public originalText: string,
    public replacement: string,
    public description: string,
    public executedAt: string,
    public language: string
  ) {}

  get characterCount() {
    return this.replacement.length;
  }
}
